import { useCallback } from 'react';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { useShouldShowEmbeddedConfluence } from './hooks/useShouldShowEmbeddedConfluence';
import { type CreateEmbeddedPage, type Cohort, BLANK_PAGE } from './types';
import { type BlueprintData } from '../../../state/ui/blueprints/types';
import { type ConfluenceTemplate } from '../../../containers/template/types';
import { fireErrorAnalytics } from '@atlassian/error-handling';
import { fireExposure } from './utils';
import { ItemModuleCompleteKeys } from '../../../common/types';

type Props = {
	contentId: string | null;
	isConfluenceActivationCompleteForPreselectedTemplate: boolean;
	blueprints: BlueprintData[];
	createEmbeddedPage: CreateEmbeddedPage;
};

export const SelectedTemplatePreview = ({
	contentId,
	blueprints,
	isConfluenceActivationCompleteForPreselectedTemplate,
	createEmbeddedPage,
}: Props): JSX.Element | null => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const showEmbeddedConfluencePage = useCallback(
		(template: ConfluenceTemplate) => {
			try {
				if (
					template.key === BLANK_PAGE ||
					template.itemModuleCompleteKey === ItemModuleCompleteKeys.BLANK_PAGE
				) {
					createEmbeddedPage(contentId || undefined, true, undefined, undefined);
				} else {
					const blueprint = blueprints.find(
						(blueprint) =>
							blueprint.itemModuleCompleteKey === template.blueprintModuleCompleteKey ||
							blueprint.blueprintModuleCompleteKey === template.blueprintModuleCompleteKey,
					);
					if (blueprint) {
						createEmbeddedPage(contentId || undefined, true, blueprint);
					} else {
						fireErrorAnalytics({
							error: new Error(
								`Embedded page was not showing due to blueprint with id: ${template.blueprintModuleCompleteKey} not found.`,
							),
							meta: {
								id: 'project-pages-embedded-confluence-error',
								packageName: '@atlassiansox/project-pages',
							},
						});
					}
				}
				return true;
			} catch (error) {
				fireErrorAnalytics({
					error: new Error(
						`Unknown error when calling createEmbeddedPage for template: ${template.blueprintModuleCompleteKey}`,
					),
					attributes: {
						cause: error instanceof Error ? error.message : 'Not known',
					},
					meta: {
						id: 'project-pages-embedded-confluence-error',
						packageName: '@atlassiansox/project-pages',
					},
				});
				return true;
			}
		},
		[contentId, createEmbeddedPage, blueprints],
	);
	const onShouldShowEmbeddedConfluence = useCallback(
		(template: ConfluenceTemplate) => {
			const cohort = FeatureGates.getExperimentValue<Cohort>(
				'project_pages_embedded_confluence_experiment',
				'cohort',
				'not-enrolled',
			);
			const isInVariation = cohort === 'variation';
			fireExposure({
				event: createAnalyticsEvent({
					type: 'sendTrackEvent',
					action: 'viewed',
				}),
				experimentType: 'ab',
				cohort,
			});
			if (isInVariation) {
				return showEmbeddedConfluencePage(template);
			}
			return true;
		},
		[createAnalyticsEvent, showEmbeddedConfluencePage],
	);

	useShouldShowEmbeddedConfluence({
		isConfluenceActivationCompleteForPreselectedTemplate,
		onShouldShowEmbeddedConfluence,
	});

	return null;
};
